import { useState } from 'react';
import { Tooltip, Box, Stack, Typography } from '@mui/joy';
import { ClickAwayListener, useMediaQuery } from '@mui/material';
import { convertToGrams } from '@/features/units/conversion';

// Shows a matched ingredient's macros for the amount/unit currently shown in
// its row (i.e. already scaled to the selected servings and converted to the
// preferred unit system), plus its share of the recipe's total calories.
// Hover to open where the pointer can hover; tap to open (and tap away to
// close) on touch devices, which is what a tablet is regardless of how wide
// its screen is.
export function IngredientNutritionTooltip({ ingredient, amount, unit, totalKcal, children }) {
  const [open, setOpen] = useState(false);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');

  const matchedIngredient = ingredient.closest_ingredients?.[0];
  const grams = matchedIngredient ? convertToGrams(amount, unit, matchedIngredient) : 0;
  if (!matchedIngredient || grams === 0) {
    return children;
  }

  const factor = grams / 100;
  const kcal = (matchedIngredient.kcal || 0) * factor;
  const protein = (matchedIngredient.protein || 0) * factor;
  const fat = (matchedIngredient.fat || 0) * factor;
  const carbs = (matchedIngredient.carbs || 0) * factor;
  const fiber = (matchedIngredient.fiber || 0) * factor;
  const percentage = totalKcal > 0 ? Math.round((kcal / totalKcal) * 100) : 0;

  const content = (
    <Stack direction="column" gap={0.5} sx={{ minWidth: '180px' }}>
      <Typography level="body-xs" sx={{ fontWeight: '600', color: 'inherit' }}>
        {Math.round(kcal)} kcal ({percentage}% of recipe)
      </Typography>
      <Typography level="body-xs" sx={{ color: 'inherit' }}>
        Protein {Math.round(protein * 10) / 10}g · Fat {Math.round(fat * 10) / 10}g · Carbs {Math.round(carbs * 10) / 10}g · Fiber {Math.round(fiber * 10) / 10}g
      </Typography>
    </Stack>
  );

  const triggerSx = { cursor: canHover ? 'help' : 'pointer', borderBottom: '1px dotted var(--joy-palette-neutral-400)', width: 'fit-content' };

  if (!canHover) {
    return (
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Tooltip title={content} open={open} disableHoverListener disableFocusListener disableTouchListener variant="solid">
          <Box component="span" onClick={() => setOpen((prev) => !prev)} sx={triggerSx}>
            {children}
          </Box>
        </Tooltip>
      </ClickAwayListener>
    );
  }

  return (
    <Tooltip title={content} variant="solid">
      <Box component="span" sx={triggerSx}>
        {children}
      </Box>
    </Tooltip>
  );
}
